import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiTrendingUp } from "react-icons/fi";
import { useActivityAction } from "@features/admin/activity/hooks/useActivityAction";
import { UpdateActivityRequest } from "@features/admin/activity/types/activityRequests";
import { Activity } from "@features/admin/activity/types/activity";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import InfoTooltip from "@core/components/InfoTooltip";
import { useScopeId } from "@core/hooks/useRouteParamId";

type UpdateActivityFormState = {
    activity_id: number;
    activity_name: string;
    description?: string;
};

type FieldErrors = Partial<Record<keyof UpdateActivityFormState | keyof UpdateActivityRequest, string[]>>;

const EMPTY_FORM: UpdateActivityFormState = {
    activity_id: 0,
    activity_name: "",
    description: "",
};

const UpdateActivityModal: React.FC<{
    show: boolean;
    onHide: () => void;
    activity: Activity | null;
    onChanged?: () => void;
}> = ({ show, onHide, activity, onChanged }) => {
    const resolvedNationId = useScopeId() ?? 0;

    const [form, setForm] = useState<UpdateActivityFormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const [, setError] = useState<string | null>(null);
    const { busy, doActivityUpdate } = useActivityAction();


    // when the incoming activity changes, rehydrate form with safe defaults
    useEffect(() => {
        if (activity) {
            setForm({
                activity_id: activity.activity_id,
                activity_name: activity.activity_name ?? "",
                description: activity.description ?? "",
            });
        } else {
            setForm(EMPTY_FORM);
        }
    }, [activity]);

    // Check if form is unchanged
    const isUnchanged = (form: UpdateActivityFormState, original: Activity | null | undefined): boolean => {

        // If no original to compare to, consider it changed
        if (!original) return false;

        // Compare relevant fields
        return (
            (form.activity_name ?? "") === (original.activity_name ?? "") &&
            (form.description ?? "") === (original.description ?? "")
        );
    };

    // Handle modal close
    const handleClose = () => {
        setFormErrors({});
        setError(null);
        onHide();
    };

    // Handle form field changes
    const handleChange =
        (field: keyof UpdateActivityFormState) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
                const value = e.target.value;

                // Update form state
                setForm((prev) => ({
                    ...prev,
                    [field]: value,
                }));
            };

    // Handle form submission
    const handleSubmit = async () => {
        const messages: string[] = [];
        if (!activity) messages.push("No activity selected.");
        if (!form.activity_id) messages.push("Activity ID is missing.");
        if (!form.activity_name?.trim()) messages.push("Activity name is required.");
        if (activity && isUnchanged(form, activity)) messages.push("No changes to save.");
        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        try {
            setFormErrors({});
            setError(null);

            // Prepare payload
            const payload: UpdateActivityRequest = {
                name: form.activity_name,
                description: form.description,
            };

            const response = await doActivityUpdate(resolvedNationId, form.activity_id, payload);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
                onHide();
                onChanged?.();
            } else {
                const err = response.getError() as ErrorResponse;
                const nextErrors = (err?.errors as FieldErrors) ?? {};
                if (nextErrors.name && !nextErrors.activity_name) {
                    nextErrors.activity_name = nextErrors.name;
                }
                setFormErrors(nextErrors);
                showError(err);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="update-activity-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    <FiTrendingUp className="me-2" aria-hidden={true} />
                    Update Activity
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3 pb-4">
                <Form>
                    <Form.Group className="mb-3" controlId="activityName">
                        <Form.Label>Activity Name <span className="text-danger">*</span><InfoTooltip text="A short name for this activity, e.g. Sunday Service or Youth Meeting." /></Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Activity Name"
                            value={form.activity_name ?? ""}
                            onChange={handleChange("activity_name")}
                            isInvalid={!!formErrors.activity_name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.activity_name?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="activityDescription">
                        <Form.Label>Description<InfoTooltip text="Optional details about what this activity is and how it is used in reports." /></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter Activity Description"
                            value={form.description ?? ""}
                            onChange={handleChange("description")}
                            isInvalid={!!formErrors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.description?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={handleSubmit}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy ? (
                                <>
                                    <Spinner animation="border" className="me-2" size="sm" />
                                    Updating Activity...
                                </>
                            ) : (
                                "Update Activity"
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default UpdateActivityModal;
